import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { usePersons, usePerson } from "@/features/pessoas/hooks/usePersons";
import { usePersonAddresses } from "@/features/pessoas/hooks/usePersonAddresses";
import { PersonForm } from "@/features/pessoas/components/PersonForm";
import { AddressTable } from "@/features/pessoas/components/AddressTable";
import { PrecoProdutoTable } from "@/features/pessoas/components/PrecoProdutoTable";
import { usePrecosProduto } from "@/features/pessoas/hooks/usePrecosProduto";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { User, MapPin, Tag } from "lucide-react";
import type { PersonFormData } from "@/features/pessoas/schemas/personSchema";
import type { AddressFormData } from "@/features/pessoas/schemas/personSchema";
import type { ClienteDetalheApi } from "@/features/pessoas/types";

// ── Mapper: API response → Form fields ────────────────────────────────────

function mapToFormData(c: ClienteDetalheApi): PersonFormData {
  const tipoPessoa = c.tipoPessoa === 'J' ? 'juridica' : 'fisica';

  const mapGender = (s?: string | null) => {
    if (s === 'M') return 'masculino' as const;
    if (s === 'F') return 'feminino' as const;
    return undefined;
  };

  const mapCivilStatus = (ec?: string | null) => {
    switch (ec) {
      case 'S': return 'solteiro' as const;
      case 'C': return 'casado' as const;
      case 'D': return 'divorciado' as const;
      case 'V': return 'viuvo' as const;
    }
    return undefined;
  };

  return {
    person_type:           tipoPessoa,
    name:                  c.nomeRazao,
    nickname:              c.apelido        ?? undefined,
    is_active:             true,
    cpf_cnpj:              c.cpfCnpj        ?? undefined,
    ie:                    c.inscricaoEstadual  ?? undefined,
    im:                    c.inscricaoMunicipal ?? undefined,
    email:                 c.email          ?? undefined,
    phone:                 c.fone           ?? undefined,
    mobile:                c.celular        ?? undefined,
    notes:                 c.observacao     ?? undefined,
    opt_in_marketing:      c.optInMarketing ?? true,
    birth_date:            c.dtNascimento   ? String(c.dtNascimento).split('T')[0] : undefined,
    gender:                mapGender(c.sexo),
    civil_status:          mapCivilStatus(c.estadoCivil),
    is_customer:           c.isCliente,
    is_supplier:           c.isFornecedor,
    is_employee:           c.isFuncionario,
    is_driver:             c.isMotorista,
    is_sales_rep:          c.isVendedor,
    is_fleet_owner:        c.isFrotista,
    is_accountant:         c.isContador,
    is_operator:           c.isOperadora,
    is_final_consumer:     c.isConsumidorFinal,
    is_exportable:         c.isExportavel ?? true,
    credit_blocked:        c.isBloqueiasVenda,
    spc_situation:         c.situacaoSpc    ?? undefined,
    allows_credit_sales:   c.isPermiteVendaPrazo,
    credit_limit:          c.limiteBloqueioCredito,
    discount_percentage:   c.perDesconto,
    commission_percentage: c.perComissao,
    customer_group_id:     c.grupoClienteId   || undefined,
    billing_group_id:      c.grupoCobrancaId  || undefined,
    salesperson_id:        c.vendedorId       || undefined,
    price_table_id:        c.tabelaPrecoProdutoId || undefined,
    route_id:              c.rotaId           || undefined,
    country_id:            c.paisId,
    taxpayer_type_id:      c.tipoContribuinteId,
    earns_loyalty_points:  c.isPontuaFidelidade,
    loyalty_points:        c.pontos,
    loyalty_points_value:  c.valorPontos,
    invoice_due_days:      c.diasVenceFatura,
    invoice_closing_day:   c.diaFechaFatura,
    invoice_on_last_day:   c.isFaturaUltimoDiaMes,
    // Fiscal
    tax_regime:            c.regimeTributarioId ?? undefined,
    ie_indicator:          c.indicadorIe        ?? undefined,
    withhold_ir:           c.reterIr,     ir_percentage:     c.perIr,
    withhold_csll:         c.reterCsll,   csll_percentage:   c.perCsll,
    withhold_inss:         c.reterInss,   inss_percentage:   c.perInss,
    withhold_pis:          c.reterPis,    pis_percentage:    c.perPis,
    withhold_cofins:       c.reterCofins, cofins_percentage: c.perCofins,
    withhold_issqn:        c.reterIssqn,  issqn_percentage:  c.perIssqn,
    gov_agency_type:       c.tipoOrgaoGovernamental ?? undefined,
    construction_id:       c.idObra ?? undefined,
    art_id:                c.idArt  ?? undefined,
    cbs_reduction_gov:     c.perRedCbsOrgaoGoverno ?? undefined,
    // Pessoal / documentos / funcionário / recado
    profession:            c.profissao        ?? undefined,
    birthplace:            c.naturalidade     ?? undefined,
    father_name:           c.filiacaoPai      ?? undefined,
    mother_name:           c.filiacaoMae      ?? undefined,
    spouse_name:           c.nomeConjuge      ?? undefined,
    spouse_phone:          c.conjugeFone      ?? undefined,
    rg:                    c.rg               ?? undefined,
    rg_issuer:             c.rgSsp            ?? undefined,
    rg_issue_date:         c.dtRgEmissao ? String(c.dtRgEmissao).split('T')[0] : undefined,
    driver_license:        c.habilitacao      ?? undefined,
    work_card:             c.carteiraTrabalho ?? undefined,
    contact_person_name:        c.pessoaRecado            ?? undefined,
    contact_person_phone:       c.pessoaRecadoFone        ?? undefined,
    contact_person_mobile:      c.pessoaRecadoCelular     ?? undefined,
    contact_person_residential: c.pessoaRecadoResidencial ?? undefined,
    referral_person:       c.pessoaIndicador  ?? undefined,
    admission_date:        c.dtAdmissao ? String(c.dtAdmissao).split('T')[0] : undefined,
    job_function:          c.funcao           ?? undefined,
    salary:                c.salario          ?? undefined,
    // Relacionamento (CRM)
    product_classification:  c.classificacaoProduto      ?? undefined,
    desired_product:         c.produtoDesejaComprar       ?? undefined,
    last_purchase_date:      c.dtUltimaCompra ? String(c.dtUltimaCompra).split('T')[0] : undefined,
    preferred_product_id:    c.produtoPreferenciaId       ?? undefined,
    preferred_group_id:      c.grupoProdutoPreferenciaId  ?? undefined,
    how_met:                 c.comoConheceu               ?? undefined,
    complaint:               c.motivoDesagrado            ?? undefined,
    preferred_size:          c.tamanhoPreferencia         ?? undefined,
    preferred_color:         c.corPreferencia             ?? undefined,
    preferred_flavor:        c.tonalidadeSabor            ?? undefined,
    customer_classification: c.classificacaoCliente       ?? undefined,
    gave_up_purchase:        c.isDesistiuCompra           ?? false,
    placed_order:            c.isFezPedido                ?? false,
    already_served:          c.isJaAtendido               ?? false,
    needs_contact:           c.isPrecisaContato           ?? false,
    // Dados bancários do favorecido (remessa CNAB)
    supplier_bank_code:      c.bancoFavorecido            ?? undefined,
    supplier_agency:         c.agenciaFavorecido          ?? undefined,
    supplier_agency_dv:      c.agenciaDvFavorecido        ?? undefined,
    supplier_account:        c.contaFavorecido            ?? undefined,
    supplier_account_dv:     c.contaDvFavorecido          ?? undefined,
  };
}

// ── Mapper: Form fields → API request ─────────────────────────────────────

function mapToApiRequest(data: PersonFormData) {
  const mapGender = (g?: string | null) => {
    if (g === 'masculino') return 'M';
    if (g === 'feminino')  return 'F';
    return undefined;
  };

  const mapCivilStatus = (cs?: string | null) => {
    switch (cs) {
      case 'solteiro':  return 'S';
      case 'casado':    return 'C';
      case 'divorciado': return 'D';
      case 'viuvo':     return 'V';
    }
    return undefined;
  };

  return {
    nomeRazao:           data.name,
    paisId:              data.country_id       ?? 1,
    tipoContribuinteId:  data.taxpayer_type_id ?? 1,
    apelido:             data.nickname         || undefined,
    cpfCnpj:             data.cpf_cnpj         || undefined,
    inscricaoEstadual:   data.ie               || undefined,
    inscricaoMunicipal:  data.im               || undefined,
    tipoPessoa:          data.person_type === 'juridica' ? 'J' : 'F',
    sexo:                mapGender(data.gender),
    estadoCivil:         mapCivilStatus(data.civil_status),
    dtNascimento:        data.birth_date        || undefined,
    fone:                data.phone             || undefined,
    celular:             data.mobile            || undefined,
    email:               data.email             || undefined,
    observacao:          data.notes             || undefined,
    optInMarketing:      data.opt_in_marketing,
    isCliente:           data.is_customer        ?? false,
    isFornecedor:        data.is_supplier        ?? false,
    isFuncionario:       data.is_employee        ?? false,
    isMotorista:         data.is_driver          ?? false,
    isVendedor:          data.is_sales_rep       ?? false,
    isFrotista:          data.is_fleet_owner     ?? false,
    isOperadora:         data.is_operator        ?? false,
    isConsumidorFinal:   data.is_final_consumer  ?? false,
    isBloqueiasVenda:    data.credit_blocked     ?? false,
    isPermiteVendaPrazo: data.allows_credit_sales ?? true,
    limiteBloqueioCredito: data.credit_limit     ?? 0,
    perDesconto:         data.discount_percentage  ?? 0,
    perComissao:         data.commission_percentage ?? 0,
    grupoClienteId:      data.customer_group_id  ?? 1,
    grupoCobrancaId:     data.billing_group_id   ?? 0,
    vendedorId:          data.salesperson_id     ?? 0,
    tabelaPrecoProdutoId: data.price_table_id    ?? 0,
    rotaId:              data.route_id           ?? 0,
    // Fiscal
    regimeTributarioId:  data.tax_regime         ?? undefined,
    indicadorIe:         data.ie_indicator       ?? undefined,
    reterIr:     data.withhold_ir     ?? false, perIr:     data.ir_percentage     ?? 0,
    reterCsll:   data.withhold_csll   ?? false, perCsll:   data.csll_percentage   ?? 0,
    reterInss:   data.withhold_inss   ?? false, perInss:   data.inss_percentage   ?? 0,
    reterPis:    data.withhold_pis    ?? false, perPis:    data.pis_percentage    ?? 0,
    reterCofins: data.withhold_cofins ?? false, perCofins: data.cofins_percentage ?? 0,
    reterIssqn:  data.withhold_issqn  ?? false, perIssqn:  data.issqn_percentage  ?? 0,
    tipoOrgaoGovernamental: data.gov_agency_type ?? undefined,
    idObra:              data.construction_id    || undefined,
    idArt:               data.art_id             || undefined,
    perRedCbsOrgaoGoverno: data.cbs_reduction_gov ?? undefined,
    // Papéis + pessoal / documentos / funcionário / recado
    isContador:          data.is_accountant      ?? false,
    isExportavel:        data.is_exportable      ?? true,
    profissao:           data.profession         || undefined,
    naturalidade:        data.birthplace         || undefined,
    filiacaoPai:         data.father_name        || undefined,
    filiacaoMae:         data.mother_name        || undefined,
    nomeConjuge:         data.spouse_name        || undefined,
    conjugeFone:         data.spouse_phone       || undefined,
    rg:                  data.rg                 || undefined,
    rgSsp:               data.rg_issuer          || undefined,
    dtRgEmissao:         data.rg_issue_date      || undefined,
    habilitacao:         data.driver_license     || undefined,
    carteiraTrabalho:    data.work_card          || undefined,
    pessoaRecado:            data.contact_person_name        || undefined,
    pessoaRecadoFone:        data.contact_person_phone       || undefined,
    pessoaRecadoCelular:     data.contact_person_mobile      || undefined,
    pessoaRecadoResidencial: data.contact_person_residential || undefined,
    pessoaIndicador:     data.referral_person    || undefined,
    dtAdmissao:          data.admission_date     || undefined,
    funcao:              data.job_function       || undefined,
    salario:             data.salary             ?? undefined,
    // Relacionamento (CRM)
    classificacaoProduto:      data.product_classification  || undefined,
    produtoDesejaComprar:      data.desired_product         || undefined,
    dtUltimaCompra:            data.last_purchase_date      || undefined,
    produtoPreferenciaId:      data.preferred_product_id    ?? undefined,
    grupoProdutoPreferenciaId: data.preferred_group_id      ?? undefined,
    comoConheceu:              data.how_met                 || undefined,
    motivoDesagrado:           data.complaint               || undefined,
    tamanhoPreferencia:        data.preferred_size          || undefined,
    corPreferencia:            data.preferred_color         || undefined,
    tonalidadeSabor:           data.preferred_flavor        || undefined,
    classificacaoCliente:      data.customer_classification || undefined,
    isDesistiuCompra:          data.gave_up_purchase        ?? false,
    isFezPedido:               data.placed_order            ?? false,
    isJaAtendido:              data.already_served          ?? false,
    isPrecisaContato:          data.needs_contact           ?? false,
    // Dados bancários do favorecido (remessa CNAB)
    bancoFavorecido:           data.supplier_bank_code      || undefined,
    agenciaFavorecido:         data.supplier_agency         || undefined,
    agenciaDvFavorecido:       data.supplier_agency_dv      || undefined,
    contaFavorecido:           data.supplier_account        || undefined,
    contaDvFavorecido:         data.supplier_account_dv     || undefined,
  };
}

// ── Page ──────────────────────────────────────────────────────────────────

const PersonFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { createPerson, updatePerson, isCreating, isUpdating } = usePersons();
  const { data: pessoa, isLoading } = usePerson(id);
  const {
    addresses,
    createAddress,
    updateAddress,
    deleteAddress,
    isCreating: isCreatingAddress,
    isUpdating: isUpdatingAddress,
  } = usePersonAddresses(id);
  const { precos, createPreco, deletePreco, isCreating: isCreatingPreco } = usePrecosProduto(id);

  const handleSubmit = (data: PersonFormData) => {
    const req = mapToApiRequest(data);
    if (id) {
      updatePerson({ id, data: req }, { onSuccess: () => navigate("/pessoas") });
    } else {
      createPerson({ person: req }, { onSuccess: () => navigate("/pessoas") });
    }
  };

  const mapAddressFormToApi = (data: AddressFormData) => ({
    tipo:         data.address_type,
    logradouro:   data.street,
    numero:       data.number    ?? "",
    bairro:       data.neighborhood ?? "",
    cidade:       data.city,
    uf:           data.state,
    cep:          data.zip_code  ?? "",
    complemento:  data.complement ?? undefined,
    cidadeIbgeId: data.ibge_city_id ?? 0,
    paisId:       data.country_id   ?? 1,
    distancia:    data.distance_km != null ? String(data.distance_km) : undefined,
    tempoEntrega: data.delivery_time_minutes != null ? String(data.delivery_time_minutes) : undefined,
  });

  const handleAddAddress = (data: AddressFormData) => {
    createAddress(mapAddressFormToApi(data));
  };

  const handleUpdateAddress = (addressId: number, data: AddressFormData) => {
    updateAddress({ id: addressId, data: mapAddressFormToApi(data) });
  };

  const handleDeleteAddress = (addressId: number) => {
    deleteAddress(addressId);
  };

  if (id && isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  // Map API data to form data for editing
  const formDefaultValues = pessoa ? mapToFormData(pessoa) : undefined;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/pessoas")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            {id ? (pessoa?.nomeRazao ?? "Editar Pessoa") : "Nova Pessoa"}
          </h1>
          {id && <p className="text-sm text-muted-foreground">Cadastro de pessoas e empresas</p>}
        </div>
      </div>

      <Tabs defaultValue="dados" className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-2xl">
          <TabsTrigger value="dados" className="gap-2">
            <User className="h-4 w-4" />
            Dados
          </TabsTrigger>
          <TabsTrigger value="enderecos" className="gap-2" disabled={!id}>
            <MapPin className="h-4 w-4" />
            Endereços {id ? `(${addresses.length})` : ""}
          </TabsTrigger>
          <TabsTrigger value="precos" className="gap-2" disabled={!id}>
            <Tag className="h-4 w-4" />
            Preço por Produto {id ? `(${precos.length})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dados" className="mt-6">
          <div className="bg-card rounded-lg border p-4 sm:p-6">
            <PersonForm
              onSubmit={handleSubmit}
              defaultValues={formDefaultValues}
              isLoading={isCreating || isUpdating}
            />
          </div>
        </TabsContent>

        <TabsContent value="enderecos" className="mt-6">
          <div className="bg-card rounded-lg border p-4 sm:p-6">
            <AddressTable
              addresses={addresses}
              personId={id}
              onAdd={handleAddAddress}
              onUpdate={handleUpdateAddress}
              onDelete={handleDeleteAddress}
              isLoading={isCreatingAddress || isUpdatingAddress}
            />
          </div>
        </TabsContent>

        <TabsContent value="precos" className="mt-6">
          <div className="bg-card rounded-lg border p-4 sm:p-6">
            <PrecoProdutoTable
              precos={precos}
              onAdd={createPreco}
              onDelete={deletePreco}
              isLoading={isCreatingPreco}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PersonFormPage;
